import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { AdType, MinimumAdSize } from '../../constants/ads';
import useIsAdmin from '../../hooks/useIsAdmin';
import { BidBarrelStoreAction } from '../../redux/bidbarrel/bidBarrelReducer';
import { boxModelStyles, IBoxModelStyleProps } from '../../styles/css';

interface IStyledAdUnitProps {
  $baseHeight?: number;
  $baseWidth?: number;
}

const StyledAdUnit = styled.div<IStyledAdUnitProps>`
  ${boxModelStyles}
  display: flex;
  justify-content: center;
  ${props => (props.$baseHeight ? `min-height: ${props.$baseHeight}px` : '')};
  ${props => (props.$baseWidth ? `min-width: ${props.$baseWidth}px` : '')};
`;

export interface IAdUnitProps extends IBoxModelStyleProps {
  code: AdType;
  incIndex?: number; // 1-indexed, if applicable
  holdWidth?: boolean;
  holdHeight?: boolean;
  isOutOfPage?: boolean;
}

export interface IPresetAdUnitProps extends Omit<IAdUnitProps, 'code'> {}

const AdUnit: React.FC<IAdUnitProps> = ({
  code,
  incIndex,
  holdWidth = false,
  holdHeight = true,
  isOutOfPage = false,
  ...props
}) => {
  const { appType } = useRequestContext();
  const isAdmin = useIsAdmin();
  const dispatch = useDispatch();

  useEffect(() => {
    const payload = { id: code, incIndex, isOutOfPage };

    // register the ad
    dispatch({
      type: BidBarrelStoreAction.DefineAd,
      payload: { ...payload } // not sure if AdManager is mucking with the payload. Spreading is just safer
    });

    // unregister the ad
    return () => {
      dispatch({
        type: BidBarrelStoreAction.DestroyAd,
        payload: payload
      });
    };

    // This will not rerender ads when the screen size changes and it forces a new ad code
  }, [code, incIndex, dispatch, isOutOfPage]);

  // Make sure nothing is rendered, even when BidBarrel is disabled
  // Cannot check for BB initialization because it causes CLS on web
  if (appType) return null;

  return (
    <StyledAdUnit
      id={`${code}${incIndex ?? ''}`}
      className={`ad ad-container ${code.replace(' ', '-')}`}
      $baseHeight={
        (!isAdmin && holdHeight && MinimumAdSize.get(code)?.[1]) || undefined
      }
      $baseWidth={
        (!isAdmin && holdWidth && MinimumAdSize.get(code)?.[0]) || undefined
      }
      {...props}
    />
  );
};

export default AdUnit;
